import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { vehicleAPI } from '../api/client';
import { Car, Plus, Calendar, Fuel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { VehiclePhoto } from '../components/VehiclePhoto';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.list();
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Vehicles</h1>
          <p className="text-muted-foreground mt-1">Manage your vehicle fleet</p>
        </div>
        <Button asChild size="lg" className="rounded-xl" data-testid="add-vehicle-button">
          <Link to="/app/vehicles/add">
            <Plus className="h-5 w-5 mr-2" />
            Add Vehicle
          </Link>
        </Button>
      </div>

      {vehicles.length > 0 ? (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        >
          {vehicles.map((vehicle) => (
            <motion.div key={vehicle.vehicle_id} variants={itemVariants}>
              <Link to={`/app/vehicles/${vehicle.vehicle_id}`}>
                <Card className="card-elevated card-hover cursor-pointer overflow-hidden h-full">
                  {/* Vehicle photo (IONOS presigned URL) with icon fallback */}
                  <VehiclePhoto
                    vehicleId={vehicle.vehicle_id}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="h-40 w-full overflow-hidden"
                  />
                  
                  <CardHeader>
                    <div className="space-y-2">
                      <CardTitle className="text-xl">
                        {vehicle.make} {vehicle.model}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">
                          {vehicle.registration || vehicle.reg_plate}
                        </Badge>
                        <span className="text-sm text-muted-foreground">{vehicle.year}</span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3 text-sm">
                    {vehicle.mot_expiry && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>MOT: {format(parseISO(vehicle.mot_expiry), 'dd MMM yyyy')}</span>
                      </div>
                    )}
                    {vehicle.fuel_type && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Fuel className="h-4 w-4" />
                        <span>{vehicle.fuel_type}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="card-elevated">
            <CardContent className="py-16 text-center">
              <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Car className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold text-2xl mb-2">No vehicles yet</h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Add your first vehicle to start tracking maintenance, expenses, and important reminders.
              </p>
              <Button asChild size="lg" className="rounded-xl">
                <Link to="/app/vehicles/add">
                  <Plus className="h-5 w-5 mr-2" />
                  Add Your First Vehicle
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Vehicles;
